import { useTheme } from "@emotion/react";
import { LoadingButton } from "@mui/lab";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Slide,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useFormik } from "formik";
import { useContext, useEffect, useState } from "react";
import * as yup from "yup";
import { AuthContext } from "../../../context/AuthContext";

const quickbooksCustomerSchema = yup.object({
  Title: yup.string().max(16, "16 characters max"), // title
  GivenName: yup.string().max(100, "100 characters max"), // first name
  MiddleName: yup.string().max(100, "100 characters max"), // middle name
  FamilyName: yup.string().max(100, "100 characters max"), // last name
  Suffix: yup.string().max(16, "16 characters max"), // suffix
  CompanyName: yup.string().max(100, "100 characters max"), // company name
  DisplayName: yup.string().max(500, "500 characters max"), // customer display name
  PrimaryEmailAddr: yup.object({
    Address: yup.string().email("Invalid Email").max(100, "100 characters max"),
  }), // email
  PrimaryPhone: yup.object({
    FreeFormNumber: yup.string().max(30, "30 characters max"),
  }), // phone number
  Mobile: yup.object({
    FreeFormNumber: yup.string().max(30, "30 characters max"),
  }), // mobile number
  Fax: yup.object({
    FreeFormNumber: yup.string().max(30, "30 characters max"),
  }), // Fax
  WebAddr: yup.object({
    URI: yup
      .string()
      .url("Must be a valid url")
      .optional()
      .max(1000, "1000 characters max"), // website
  }), // website

  PrintOnCheckName: yup.string().max(110, "110 characters max"), // name to print on checks
  Job: yup.boolean(), // isASubCustomer: boolean
  BillAddr: yup.object({
    Line1: yup.string().max(500, "500 characters max"), // Street address 1
    Line2: yup.string().max(500, "500 characters max"), // Street address 2
    City: yup.string().max(255, "255 characters max"), // city
    CountrySubDivisionCode: yup.string().max(255, "255 characters max"), // State
    PostalCode: yup.string().max(30, "30 characters max"), // ZIP code
    Country: yup.string().max(255, "255 characters max"), //Country - "USA"
  }),
  ShipAddr: yup.object({
    Line1: yup.string().max(500, "500 characters max"), // Street address 1
    Line2: yup.string().max(500, "500 characters max"), // Street address 2
    City: yup.string().max(255, "255 characters max"), // city
    CountrySubDivisionCode: yup.string().max(255, "255 characters max"), // State
    PostalCode: yup.string().max(30, "30 characters max"), // ZIP code
    Country: yup.string().max(255, "255 characters max"), //Country - "USA"
  }),
  shippingSameAsBilling: yup.boolean(),
});

const AddQuickbooksCustomerDialog = ({ open, onClose, onSaved }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { authToken, ezorder } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleClose = () => {
    formik.resetForm();
    setErrorMessage("");
    onClose();
  };

  const initialValues = {
    Title: "", // title
    GivenName: "", // first name
    MiddleName: "", // middle name
    FamilyName: "", // last name
    Suffix: "", // suffix
    CompanyName: "", // company name
    DisplayName: "", // customer display name
    PrimaryEmailAddr: {
      Address: "",
    }, // email
    PrimaryPhone: {
      FreeFormNumber: "",
    }, // phone number
    Mobile: {
      FreeFormNumber: "",
    }, // mobile number
    Fax: {
      FreeFormNumber: "",
    }, // fax
    WebAddr: {
      URI: "",
    }, // website
    PrintOnCheckName: "", // name to print on checks
    Job: false, // isASubCustomer: boolean
    BillAddr: {
      Line1: "", // Street address 1
      Line2: "", // Street address 2
      City: "", // city
      CountrySubDivisionCode: "", // State
      PostalCode: "", // ZIP code
      Country: "", //Country - "USA"
    },
    ShipAddr: {
      Line1: "", // Street address 1
      Line2: "", // Street address 2
      City: "", // city
      CountrySubDivisionCode: "", // State
      PostalCode: "", // ZIP code
      Country: "", //Country - "USA"
    },
    shippingSameAsBilling: true,
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: quickbooksCustomerSchema,
    // enableReinitialize: true,
    onSubmit: () => {
      console.log("TRYING TO SUBMIT");

      //   remove shippingSameAsBilling
      let { shippingSameAsBilling, ...customer } = formik.values;
      //   IF SHIPPING SAME AS BILLING, COPY BILLING INTO SHIPPING ADDR BEFORE SUBMIT
      if (shippingSameAsBilling) {
        customer.ShipAddr = { ...customer.BillAddr };
      }
      handleSave(customer);
    },
  });

  const handleSave = async (customerData) => {
    try {
      setLoading(true);
      const header = {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      };
      // const body = {
      //   searchQuery: search,
      //   searchCategory: category,
      // };
      const response = await ezorder.post(
        `/quickbooks/customers/create`,
        customerData,
        header
      );

      if (response.status == 200 || response.status === 201) {
        setErrorMessage("");
        onSaved(response.data.customer);
      } else {
        if (
          response &&
          response.response &&
          response.response.data &&
          response.response.data.error
        ) {
          setErrorMessage(response.response.data.error);
        } else {
          setErrorMessage("Something went wrong");
        }
      }
    } catch (error) {
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        if (error.response.data?.error) {
          setErrorMessage(error.response.data.error);
        } else {
          setErrorMessage("Internal server error");
        }
      } else {
        setErrorMessage("Internal server error");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (formik) console.log(formik.values);
  }, [formik.values]);

  //   useEffect(() => {
  //     if (formik) console.log(formik.errors);
  //   }, [formik.errors]);

  return (
    <Dialog
      fullScreen={isSmallScreen}
      maxWidth="md"
      fullWidth={true}
      open={open}
      onClose={() => {
        if (!loading) handleClose();
      }}
    >
      <DialogTitle>New Quickbooks Customer</DialogTitle>
      <DialogContent>
        <DialogContentText>Name and Contact</DialogContentText>
        <Box
          sx={{
            display: "flex",
            flexDirection: isSmallScreen ? "column" : "row",
          }}
        >
          <TextField
            size="small"
            sx={{ marginTop: "8px", marginRight: "8px" }}
            label="Title"
            id="Title"
            name="Title"
            value={formik.values.Title}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.Title && Boolean(formik.errors.Title)}
            helperText={formik.errors.Title && `${formik.errors.Title}`}
            onFocus={(e) => {
              e.target.select();
            }}
          />
          <TextField
            size="small"
            sx={{ marginTop: "8px", marginRight: "8px" }}
            label="First Name"
            id="GivenName"
            name="GivenName"
            value={formik.values.GivenName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.GivenName && Boolean(formik.errors.GivenName)}
            helperText={formik.errors.GivenName && `${formik.errors.GivenName}`}
            onFocus={(e) => {
              e.target.select();
            }}
          />
          <TextField
            size="small"
            sx={{ marginTop: "8px", marginRight: "8px" }}
            label="Middle Name"
            id="MiddleName"
            name="MiddleName"
            value={formik.values.MiddleName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.MiddleName && Boolean(formik.errors.MiddleName)
            }
            helperText={
              formik.errors.MiddleName && `${formik.errors.MiddleName}`
            }
            onFocus={(e) => {
              e.target.select();
            }}
          />
          <TextField
            size="small"
            sx={{ marginTop: "8px", marginRight: "8px" }}
            label="Last Name"
            id="FamilyName"
            name="FamilyName"
            value={formik.values.FamilyName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.FamilyName && Boolean(formik.errors.FamilyName)
            }
            helperText={
              formik.errors.FamilyName && `${formik.errors.FamilyName}`
            }
            onFocus={(e) => {
              e.target.select();
            }}
          />
          <TextField
            size="small"
            sx={{ marginTop: "8px", marginRight: "8px", maxWidth: "80px" }}
            label="Suffix"
            id="Suffix"
            name="Suffix"
            value={formik.values.Suffix}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.Suffix && Boolean(formik.errors.Suffix)}
            helperText={formik.errors.Suffix && `${formik.errors.Suffix}`}
          />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "row" }}>
          <TextField
            size="small"
            sx={{ marginTop: "8px", marginRight: "8px", flex: 1 }}
            label="Company Name"
            id="CompanyName"
            name="CompanyName"
            value={formik.values.CompanyName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.CompanyName && Boolean(formik.errors.CompanyName)
            }
            helperText={
              formik.errors.CompanyName && `${formik.errors.CompanyName}`
            }
          />
          <TextField
            size="small"
            sx={{ marginTop: "8px", marginRight: "8px", flex: 1 }}
            label="Customer Display Name *"
            id="DisplayName"
            name="DisplayName"
            value={formik.values.DisplayName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.DisplayName && Boolean(formik.errors.DisplayName)
            }
            helperText={
              formik.errors.DisplayName && `${formik.errors.DisplayName}`
            }
          />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "row" }}>
          <TextField
            size="small"
            sx={{ marginTop: "8px", marginRight: "8px", flex: 1 }}
            label="Email"
            id="PrimaryEmailAddr"
            name="PrimaryEmailAddr"
            value={formik.values.PrimaryEmailAddr.Address}
            onChange={(e) => {
              formik.setFieldValue("PrimaryEmailAddr", {
                Address: e.target.value,
              });
            }}
            onBlur={formik.handleBlur}
            error={
              formik.errors.PrimaryEmailAddr &&
              formik.errors.PrimaryEmailAddr.Address &&
              Boolean(
                formik.errors.PrimaryEmailAddr &&
                  formik.errors.PrimaryEmailAddr.Address
              )
            }
            helperText={
              formik.errors.PrimaryEmailAddr &&
              formik.errors.PrimaryEmailAddr.Address &&
              `${formik.errors.PrimaryEmailAddr.Address}`
            }
          />
          <TextField
            size="small"
            sx={{ marginTop: "8px", marginRight: "8px", flex: 1 }}
            label="Phone Number"
            id="PrimaryPhone"
            name="PrimaryPhone"
            value={formik.values.PrimaryPhone.FreeFormNumber}
            onChange={(e) => {
              formik.setFieldValue("PrimaryPhone", {
                FreeFormNumber: e.target.value,
              });
            }}
            onBlur={formik.handleBlur}
            error={
              formik.errors.PrimaryPhone &&
              formik.errors.PrimaryPhone.FreeFormNumber &&
              Boolean(
                formik.errors.PrimaryPhone &&
                  formik.errors.PrimaryPhone.FreeFormNumber
              )
            }
            helperText={
              formik.errors.PrimaryPhone &&
              formik.errors.PrimaryPhone.FreeFormNumber &&
              `${formik.errors.PrimaryPhone.FreeFormNumber}`
            }
          />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "row" }}>
          <TextField
            size="small"
            sx={{ marginTop: "8px", marginRight: "8px", flex: 1 }}
            label="Mobile Number"
            id="Mobile"
            name="Mobile"
            value={formik.values.Mobile.FreeFormNumber}
            onChange={(e) => {
              formik.setFieldValue("Mobile", {
                FreeFormNumber: e.target.value,
              });
            }}
            onBlur={formik.handleBlur}
            error={
              formik.errors.Mobile &&
              formik.errors.Mobile.FreeFormNumber &&
              Boolean(
                formik.errors.Mobile && formik.errors.Mobile.FreeFormNumber
              )
            }
            helperText={
              formik.errors.Mobile &&
              formik.errors.Mobile.FreeFormNumber &&
              `${formik.errors.Mobile.FreeFormNumber}`
            }
          />
          <TextField
            size="small"
            sx={{ marginTop: "8px", marginRight: "8px", flex: 1 }}
            label="Fax"
            id="Fax"
            name="Fax"
            value={formik.values.Fax.FreeFormNumber}
            onChange={(e) => {
              formik.setFieldValue("Fax", {
                FreeFormNumber: e.target.value,
              });
            }}
            onBlur={formik.handleBlur}
            error={
              formik.errors.Fax &&
              formik.errors.Fax.FreeFormNumber &&
              Boolean(formik.errors.Fax && formik.errors.Fax.FreeFormNumber)
            }
            helperText={
              formik.errors.Fax &&
              formik.errors.Fax.FreeFormNumber &&
              `${formik.errors.Fax.FreeFormNumber}`
            }
          />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "row" }}>
          <TextField
            size="small"
            sx={{ marginTop: "8px", marginRight: "8px", flex: 1 }}
            label="Website"
            id="WebAddr"
            name="WebAddr"
            value={formik.values.WebAddr.URI}
            onChange={(e) => {
              formik.setFieldValue("WebAddr", {
                URI: e.target.value,
              });
            }}
            onBlur={formik.handleBlur}
            error={
              formik.errors.WebAddr &&
              formik.errors.WebAddr.URI &&
              Boolean(formik.errors.WebAddr && formik.errors.WebAddr.URI)
            }
            helperText={
              formik.errors.WebAddr &&
              formik.errors.WebAddr.URI &&
              `${formik.errors.WebAddr.URI}`
            }
          />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "row" }}>
          <TextField
            size="small"
            sx={{ marginTop: "8px", marginRight: "8px", flex: 1 }}
            label="Name To Print on Checks"
            id="PrintOnCheckName"
            name="PrintOnCheckName"
            value={formik.values.PrintOnCheckName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.PrintOnCheckName &&
              Boolean(formik.errors.PrintOnCheckName)
            }
            helperText={
              formik.errors.PrintOnCheckName &&
              `${formik.errors.PrintOnCheckName}`
            }
          />
        </Box>

        <DialogContentText sx={{ marginTop: "16px" }}>
          Addresses
        </DialogContentText>
        <Box sx={{ padding: "16px" }}>
          <Typography>Billing Address</Typography>
          <Box sx={{ display: "flex", flexDirection: "row" }}>
            <TextField
              size="small"
              sx={{ marginTop: "8px", marginRight: "8px", flex: 1 }}
              label="Street Address 1"
              id="Line1"
              name="Line1"
              value={formik.values.BillAddr.Line1}
              onChange={(e) => {
                let copy = { ...formik.values.BillAddr, Line1: e.target.value };
                formik.setFieldValue("BillAddr", copy);
              }}
              onBlur={formik.handleBlur}
              error={
                formik.errors.BillAddr &&
                formik.errors.BillAddr.Line1 &&
                Boolean(formik.errors.BillAddr && formik.errors.BillAddr.Line1)
              }
              helperText={
                formik.errors.BillAddr &&
                formik.errors.BillAddr.Line1 &&
                `${formik.errors.BillAddr.Line1}`
              }
            />
            <TextField
              size="small"
              sx={{ marginTop: "8px", marginRight: "8px", flex: 1 }}
              label="Street Address 2"
              id="Line2"
              name="Line2"
              value={formik.values.BillAddr.Line2}
              onChange={(e) => {
                let copy = { ...formik.values.BillAddr, Line2: e.target.value };
                formik.setFieldValue("BillAddr", copy);
              }}
              onBlur={formik.handleBlur}
              error={
                formik.errors.BillAddr &&
                formik.errors.BillAddr.Line2 &&
                Boolean(formik.errors.BillAddr && formik.errors.BillAddr.Line2)
              }
              helperText={
                formik.errors.BillAddr &&
                formik.errors.BillAddr.Line2 &&
                `${formik.errors.BillAddr.Line2}`
              }
            />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "row" }}>
            <TextField
              size="small"
              sx={{ marginTop: "8px", marginRight: "8px", flex: 1 }}
              label="City"
              id="City"
              name="City"
              value={formik.values.BillAddr.City}
              onChange={(e) => {
                let copy = { ...formik.values.BillAddr, City: e.target.value };
                formik.setFieldValue("BillAddr", copy);
              }}
              onBlur={formik.handleBlur}
              error={
                formik.errors.BillAddr &&
                formik.errors.BillAddr.City &&
                Boolean(formik.errors.BillAddr && formik.errors.BillAddr.City)
              }
              helperText={
                formik.errors.BillAddr &&
                formik.errors.BillAddr.City &&
                `${formik.errors.BillAddr.City}`
              }
            />
            <TextField
              autocomplete="address-level1"
              size="small"
              sx={{ marginTop: "8px", marginRight: "8px", flex: 1 }}
              label="State"
              id="CountrySubDivisionCode"
              name="CountrySubDivisionCode"
              value={formik.values.BillAddr.CountrySubDivisionCode}
              onChange={(e) => {
                let copy = {
                  ...formik.values.BillAddr,
                  CountrySubDivisionCode: e.target.value,
                };
                formik.setFieldValue("BillAddr", copy);
              }}
              onBlur={formik.handleBlur}
              error={
                formik.errors.BillAddr &&
                formik.errors.BillAddr.CountrySubDivisionCode &&
                Boolean(
                  formik.errors.BillAddr &&
                    formik.errors.BillAddr.CountrySubDivisionCode
                )
              }
              helperText={
                formik.errors.BillAddr &&
                formik.errors.BillAddr.CountrySubDivisionCode &&
                `${formik.errors.BillAddr.CountrySubDivisionCode}`
              }
            />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "row" }}>
            <TextField
              size="small"
              sx={{ marginTop: "8px", marginRight: "8px", flex: 1 }}
              label="ZIP Code"
              id="PostalCode"
              name="PostalCode"
              value={formik.values.BillAddr.PostalCode}
              onChange={(e) => {
                let copy = {
                  ...formik.values.BillAddr,
                  PostalCode: e.target.value,
                };
                formik.setFieldValue("BillAddr", copy);
              }}
              onBlur={formik.handleBlur}
              error={
                formik.errors.BillAddr &&
                formik.errors.BillAddr.PostalCode &&
                Boolean(
                  formik.errors.BillAddr && formik.errors.BillAddr.PostalCode
                )
              }
              helperText={
                formik.errors.BillAddr &&
                formik.errors.BillAddr.PostalCode &&
                `${formik.errors.BillAddr.PostalCode}`
              }
            />
            <TextField
              size="small"
              sx={{ marginTop: "8px", marginRight: "8px", flex: 1 }}
              label="Country"
              id="Country"
              name="Country"
              value={formik.values.BillAddr.Country}
              onChange={(e) => {
                let copy = {
                  ...formik.values.BillAddr,
                  Country: e.target.value,
                };
                formik.setFieldValue("BillAddr", copy);
              }}
              onBlur={formik.handleBlur}
              error={
                formik.errors.BillAddr &&
                formik.errors.BillAddr.Country &&
                Boolean(
                  formik.errors.BillAddr && formik.errors.BillAddr.Country
                )
              }
              helperText={
                formik.errors.BillAddr &&
                formik.errors.BillAddr.Country &&
                `${formik.errors.BillAddr.Country}`
              }
            />
          </Box>
        </Box>
        <Box sx={{ padding: "16px" }}>
          <Typography>Shipping Address</Typography>
          <Box sx={{ display: "flex", flexDirection: "row", marginTop: "8px" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                magin: 0,
                padding: 0,
              }}
            >
              <Checkbox
                style={{
                  magin: 0,
                  padding: 0,
                }}
                checked={formik.values.shippingSameAsBilling}
                id="shippingSameAsBilling"
                name="shippingSameAsBilling"
                onChange={(e) => {
                  formik.setValues((prev) => ({
                    ...prev,
                    shippingSameAsBilling: e.target.checked,
                    ShipAddr: {
                      Line1: "", // Street address 1
                      Line2: "", // Street address 2
                      City: "", // city
                      CountrySubDivisionCode: "", // State
                      PostalCode: "", // ZIP code
                      Country: "", //Country - "USA"
                    },
                  }));
                }}
              />
              <Typography>Same as Billing</Typography>
            </Box>
          </Box>
          {formik.values.shippingSameAsBilling === false && (
            <Box>
              <Box sx={{ display: "flex", flexDirection: "row" }}>
                <TextField
                  size="small"
                  sx={{ marginTop: "8px", marginRight: "8px", flex: 1 }}
                  label="Street Address 1"
                  id="Line1"
                  name="Line1"
                  value={formik.values.ShipAddr.Line1}
                  onChange={(e) => {
                    let copy = {
                      ...formik.values.ShipAddr,
                      Line1: e.target.value,
                    };
                    formik.setFieldValue("ShipAddr", copy);
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.errors.ShipAddr &&
                    formik.errors.ShipAddr.Line1 &&
                    Boolean(
                      formik.errors.ShipAddr && formik.errors.ShipAddr.Line1
                    )
                  }
                  helperText={
                    formik.errors.ShipAddr &&
                    formik.errors.ShipAddr.Line1 &&
                    `${formik.errors.ShipAddr.Line1}`
                  }
                />
                <TextField
                  size="small"
                  sx={{ marginTop: "8px", marginRight: "8px", flex: 1 }}
                  label="Street Address 2"
                  id="Line2"
                  name="Line2"
                  value={formik.values.ShipAddr.Line2}
                  onChange={(e) => {
                    let copy = {
                      ...formik.values.ShipAddr,
                      Line2: e.target.value,
                    };
                    formik.setFieldValue("ShipAddr", copy);
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.errors.ShipAddr &&
                    formik.errors.ShipAddr.Line2 &&
                    Boolean(
                      formik.errors.ShipAddr && formik.errors.ShipAddr.Line2
                    )
                  }
                  helperText={
                    formik.errors.ShipAddr &&
                    formik.errors.ShipAddr.Line2 &&
                    `${formik.errors.ShipAddr.Line2}`
                  }
                />
              </Box>
              <Box sx={{ display: "flex", flexDirection: "row" }}>
                <TextField
                  size="small"
                  sx={{ marginTop: "8px", marginRight: "8px", flex: 1 }}
                  label="City"
                  id="City"
                  name="City"
                  value={formik.values.ShipAddr.City}
                  onChange={(e) => {
                    let copy = {
                      ...formik.values.ShipAddr,
                      City: e.target.value,
                    };
                    formik.setFieldValue("ShipAddr", copy);
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.errors.ShipAddr &&
                    formik.errors.ShipAddr.City &&
                    Boolean(
                      formik.errors.ShipAddr && formik.errors.ShipAddr.City
                    )
                  }
                  helperText={
                    formik.errors.ShipAddr &&
                    formik.errors.ShipAddr.City &&
                    `${formik.errors.ShipAddr.City}`
                  }
                />
                <TextField
                  size="small"
                  sx={{ marginTop: "8px", marginRight: "8px", flex: 1 }}
                  label="State"
                  id="CountrySubDivisionCode"
                  name="CountrySubDivisionCode"
                  value={formik.values.ShipAddr.CountrySubDivisionCode}
                  onChange={(e) => {
                    let copy = {
                      ...formik.values.ShipAddr,
                      CountrySubDivisionCode: e.target.value,
                    };
                    formik.setFieldValue("ShipAddr", copy);
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.errors.ShipAddr &&
                    formik.errors.ShipAddr.CountrySubDivisionCode &&
                    Boolean(
                      formik.errors.ShipAddr &&
                        formik.errors.ShipAddr.CountrySubDivisionCode
                    )
                  }
                  helperText={
                    formik.errors.ShipAddr &&
                    formik.errors.ShipAddr.CountrySubDivisionCode &&
                    `${formik.errors.ShipAddr.CountrySubDivisionCode}`
                  }
                />
              </Box>
              <Box sx={{ display: "flex", flexDirection: "row" }}>
                <TextField
                  size="small"
                  sx={{ marginTop: "8px", marginRight: "8px", flex: 1 }}
                  label="ZIP Code"
                  id="PostalCode"
                  name="PostalCode"
                  value={formik.values.ShipAddr.PostalCode}
                  onChange={(e) => {
                    let copy = {
                      ...formik.values.ShipAddr,
                      PostalCode: e.target.value,
                    };
                    formik.setFieldValue("ShipAddr", copy);
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.errors.ShipAddr &&
                    formik.errors.ShipAddr.PostalCode &&
                    Boolean(
                      formik.errors.ShipAddr &&
                        formik.errors.ShipAddr.PostalCode
                    )
                  }
                  helperText={
                    formik.errors.ShipAddr &&
                    formik.errors.ShipAddr.PostalCode &&
                    `${formik.errors.ShipAddr.PostalCode}`
                  }
                />
                <TextField
                  size="small"
                  sx={{ marginTop: "8px", marginRight: "8px", flex: 1 }}
                  label="Country"
                  id="Country"
                  name="Country"
                  value={formik.values.BillAddr.Country}
                  onChange={(e) => {
                    let copy = {
                      ...formik.values.BillAddr,
                      Country: e.target.value,
                    };
                    formik.setFieldValue("BillAddr", copy);
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.errors.BillAddr &&
                    formik.errors.BillAddr.Country &&
                    Boolean(
                      formik.errors.BillAddr && formik.errors.BillAddr.Country
                    )
                  }
                  helperText={
                    formik.errors.BillAddr &&
                    formik.errors.BillAddr.Country &&
                    `${formik.errors.BillAddr.Country}`
                  }
                />
              </Box>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            m: 1,
            position: "relative",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {errorMessage && (
            <Box>
              <Typography color={"red"}>{errorMessage}</Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          loading={loading}
          onClick={() => {
            if (!loading) handleClose();
          }}
        >
          Cancel
        </LoadingButton>
        <LoadingButton
          loading={loading}
          disabled={formik.errors && Object.keys(formik.errors).length > 0}
          onClick={() => {
            setErrorMessage("");
            formik.handleSubmit();
          }}
        >
          Submit
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddQuickbooksCustomerDialog;
